/**
 * Quels gestes l'écran propose, à chaque instant du parcours.
 *
 * Cette décision vivait dans le rendu, éparpillée en conditions imbriquées, et
 * c'est là qu'un défaut s'est glissé sans que rien ne le signale : l'écran
 * annonçait « ton image est toute noire » **et** proposait « oui, je me vois
 * bien » — une question dont on connaissait déjà la réponse, et un moyen
 * d'entrer en séance avec une caméra couverte après le lui avoir dit.
 *
 * Sortie du rendu, la règle se lit d'un coup et s'éprouve sur toutes les
 * situations, y compris celles qu'on ne pense pas à ouvrir à la main.
 *
 * Le principe qui la gouverne : **on ne demande à l'élève que ce que la machine
 * ne sait pas.** Elle sait si un flux s'ouvre, si une image est noire ou non,
 * si le micro reçoit du son. Elle ne sait pas si on entend quelque chose dans
 * la pièce. Tant qu'elle sait, elle tranche ; quand elle ne sait plus, elle
 * passe la main — et jamais l'inverse. Une image lue et non noire se valide
 * donc seule, comme le micro au premier son ; « te vois-tu bien ? » n'est
 * posé qu'à qui n'a pas d'analyse pour le dire à sa place.
 */

// Le seul import du fichier, et il reste pur : `IMAGE` est un vocabulaire, pas
// une dépendance. Le lire depuis les règles évite d'en garder deux copies.
import { IMAGE } from "./deviceCheckRules"

export const ACTION = {
    /** Demander l'accès au micro et à la caméra. */
    AUTHORIZE: "authorize",
    /** Jouer la mélodie de test. */
    PLAY_SOUND: "play-sound",
    /** « Oui » / « Non » au test du son. */
    HEARD_YES: "heard-yes",
    HEARD_NO: "heard-no",
    /** Rallumer sa vidéo depuis l'étape caméra. */
    ENABLE_VIDEO: "enable-video",
    /** Couper sa vidéo, proposé par le conseil réseau. */
    MUTE_VIDEO: "mute-video",
    /** « Oui, je me vois bien » / « Non ». */
    SEES_SELF_YES: "sees-self-yes",
    SEES_SELF_NO: "sees-self-no",
    /** « Ça n'a pas marché » : descendre d'un barreau. */
    NEXT_ATTEMPT: "next-attempt",
    /** « Je ne vois rien bouger » : couper court à l'écoute du micro. */
    MIC_NO_SIGNAL: "mic-no-signal",
    /** Ouvrir les réglages du téléphone, à la place de l'élève. */
    OPEN_SETTINGS: "open-settings",
}

/**
 * @param {object} state
 * @param {string} state.stepId          étape ouverte
 * @param {boolean} state.needsAuthorization  accès pas encore demandé
 * @param {boolean} state.isWeb
 * @param {boolean} state.isVideoMuted
 * @param {boolean} state.isObstructed   l'analyse voit une image noire
 * @param {string} state.image           où en est l'analyse, une valeur de `IMAGE`
 * @param {boolean} state.soundPlayed    la mélodie a déjà été jouée
 * @param {boolean|null} state.speakerHeard  sans effet sur les gestes proposés
 * @param {boolean|null} state.seesSelf
 * @param {string|null} state.rungId     barreau de dépannage affiché
 * @param {boolean} state.hasMoreAttempts reste-t-il un essai après celui-ci
 * @param {boolean} state.micListening   on attend que l'élève dise « allô »
 * @returns {string[]} les gestes proposés, dans l'ordre d'affichage
 */
export function visibleActions({
    stepId,
    needsAuthorization = false,
    isWeb = true,
    isVideoMuted = false,
    isObstructed = false,
    image = IMAGE.GAVE_UP,
    soundPlayed = false,
    seesSelf = null,
    rungId = null,
    hasMoreAttempts = false,
    micListening = false,
    cameraReady = true,
} = {}) {
    const actions = []

    // L'autorisation prime : sans elle, aucun autre geste n'a de sens.
    if (needsAuthorization && (stepId === "camera" || stepId === "microphone")) {
        actions.push(ACTION.AUTHORIZE)
        return actions
    }

    if (stepId === "network") {
        // Le conseil dit de couper la vidéo : encore faut-il pouvoir le faire
        // ici, l'aperçu et son bouton ne vivant qu'à l'étape caméra.
        if (rungId === "drop-video" && !isVideoMuted) actions.push(ACTION.MUTE_VIDEO)
    }

    /*
     * L'accès est bloqué : sur le web, aucun geste que l'app puisse faire.
     *
     * Une page ne peut pas rouvrir une autorisation refusée — aucune API ne
     * l'expose. Un bouton « Recharger la page » a existé ici : un élève qui ne
     * lit pas cliquait dessus à l'aveugle, et il ne changeait rien. Le
     * navigateur propose lui-même de recharger quand l'accès change, et
     * l'écran re-vérifie l'accès toutes les cinq secondes.
     *
     * Sur téléphone, le système expose une API pour ouvrir ses réglages : on y
     * emmène l'élève directement.
     */
    if (rungId === "permission" && !isWeb) {
        actions.push(ACTION.OPEN_SETTINGS)
    }

    /*
     * Pendant qu'on écoute, l'élève doit pouvoir dire que rien ne bouge.
     *
     * Sans ce geste, celui qui sait déjà que son micro est mort n'avait que
     * « Passer » — qui marque l'étape en échec — ou douze secondes à patienter
     * devant des barres immobiles. Une attente qu'on ne peut pas interrompre
     * est une impasse, même brève.
     */
    if (stepId === "microphone" && micListening) {
        actions.push(ACTION.MIC_NO_SIGNAL)
    }

    if (stepId === "speaker") {
        actions.push(ACTION.PLAY_SOUND)
        // On ne demande pas de juger un son qu'on n'a pas joué.
        if (soundPlayed) {
            actions.push(ACTION.HEARD_YES, ACTION.HEARD_NO)
        }
    }

    if (stepId === "camera") {
        if (isVideoMuted) {
            // Vidéo coupée : un seul geste possible, la rallumer.
            actions.push(ACTION.ENABLE_VIDEO)
        } else if (isObstructed) {
            // Image noire : rien à demander. Le constat est à l'écran, le geste
            // aussi. On attend que l'analyse voie l'objectif se découvrir.
        } else if (image === IMAGE.JUDGED && seesSelf === null) {
            // Image lue et non noire : l'étape se valide seule, rien à demander.
            //
            // `seesSelf === null` parce qu'une question déjà posée garde ses
            // réponses : à qui a répondu « je ne me vois pas », on laisse
            // « Toujours pas » sous la main même si l'image arrive et se
            // révèle nette. Retirer les boutons le laisserait devant un voyant
            // vert qui contredit ce qu'il vient de dire.
        } else if (cameraReady && (!isWeb || image !== IMAGE.LOOKING)) {
            // Rien n'a lu l'image — pas d'analyse, ou une analyse qui a
            // renoncé. À l'élève de dire.
            //
            // `cameraReady` d'abord : sans flux ouvert il n'y a rien à
            // regarder. L'écran proposait « oui, je me vois bien » à côté de
            // « le navigateur bloque l'accès » — la même faute que devant une
            // image noire, une question dont on connaît déjà la réponse.
            actions.push(ACTION.SEES_SELF_YES, ACTION.SEES_SELF_NO)
        }
    }

    /*
     * « Ça n'a pas marché » ne subsiste que là où l'étape ne propose pas déjà
     * le geste qui fait avancer.
     *
     * Sur le son et la caméra, c'est « Non » qui demande l'essai suivant ; une
     * vidéo coupée n'a qu'un geste, la rallumer. Un second bouton y dirait la
     * même chose — et sur une vidéo coupée il servait le barreau cadrage
     * (« installe-toi face à une fenêtre… ») à quelqu'un qui a juste éteint sa
     * caméra.
     *
     * On le LIT dans ce qui vient d'être proposé, au lieu de redériver les
     * mêmes entrées en bas de fonction. La redérivation avait un angle mort
     * coûteux : pour une caméra qui ne s'ouvre pas du tout — accès refusé,
     * aucune caméra, objectif occupé — elle concluait « l'élève tranche
     * lui-même » alors qu'on ne lui demande rien. Le bouton disparaissait,
     * l'échelle caméra ne pouvait plus avancer, et son message de fin restait
     * inatteignable.
     */
    const RESOLVING = [ACTION.HEARD_NO, ACTION.SEES_SELF_NO, ACTION.ENABLE_VIDEO]
    const answersItself = actions.some((action) => RESOLVING.includes(action))
    if (rungId && hasMoreAttempts && !answersItself) actions.push(ACTION.NEXT_ATTEMPT)

    return actions
}
